import Renderable from '../../../Component/Renderable';
import ElementComponent from '../../../Component/UI/Element';
import UITextComponent from '../../../Component/UI/Text';
import Game from '../../../Game';
import { GameObjectEvent } from '../../../GameObject/GameObjectEvent';
import { GameActions } from '../../../State/Game/GameActions';
import { ContextType } from '../../../State/Game/IContext';
import { FBTrackingEvent } from '../../../Utility/Tracking/FBTrackingEvent';
import { WinCriteriaType } from '../../../State/Level/ILevelState';
import { LevelStatus } from '../../../State/Level/ILevelState';
import { StartAnimation } from '../../Animation/AnimationHelper';
import {
    AUDIO_LEVEL_FAILED,
    AUDIO_UI_BUTTON_CLOSE,
    AUDIO_UI_BUTTON_DEFAULT,
} from '../../Audio';
import { AudioTrackType } from '../../Audio/AudioClip';
import { StartAudio } from '../../Audio/AudioHelper';
import BaseUISystem from '../BaseUISystem';
import LeaderboardHelper from '../LeaderboardHelper';
import {
    applyBounceButtonAnimation,
    applyDefaultButtonAnimation,
} from '../UIHelper';
import BaseMenu from './BaseMenu';
import { WinCriteriaTypeMap } from '../../../Constants/WinCriteria';
import { PlayerActions } from '../../../State/Player/PlayerActions';
import { ASOLILevelEventsMap } from '../../../Constants/Tracking';

const BTN_CHALLENGE: string = 'main_container/challenge_button';
const BTN_CONTINUE: string = 'main_container/continue_button';
const BTN_REPLAY: string = 'main_container/replay_button';
const BTN_CLOSE: string = 'main_container/close_button';
const TXT_TITLE: string = 'main_container/title_container/title_text';
const TOP_LIST: string = 'toplist';
const TXT_SCORE: string = 'main_container/result_score/value_text';
const TXT_COINS: string = 'main_container/result_coins/value_text';
const TXT_GOAL: string = 'main_container/goal_container/goal_cover/goal_text';
const GOAL_ICON_COMPLETE: string =
    'main_container/goal_container/goal_cover/goal_icon_complete';
const GOAL_ICON_FAIL: string =
    'main_container/goal_container/goal_cover/goal_icon_fail';

export default class LevelCompleteMenuHandler extends BaseMenu {
    private levelNumber: number;

    constructor(uiSystem: BaseUISystem) {
        super('levelCompleteMenu', uiSystem);
    }

    protected onShow(params?: any): void {
        this.popupOverlayEnabled = params.fail;

        this.levelNumber = params.levelNumber;

        // Set the popup title text.
        const titleKey = params.fail ? 'Level' : 'LevelXComplete';
        const title = this.element.find(TXT_TITLE);
        const titleText = title.getComponent(UITextComponent);
        titleText.text = Game.locale
            .getText(titleKey)
            .replace('[x]', params.levelNumber);

        // Set the goal message
        const goal = this.element.find(TXT_GOAL);
        const type = params.goals[0].type as WinCriteriaType;
        const textKey = WinCriteriaTypeMap[type].textKey;
        const text = Game.locale.getText(textKey);
        goal.getComponent(UITextComponent).text = text;

        // Show complete/fail icon.
        const goalIconComplete = this.element.find(GOAL_ICON_COMPLETE);
        goalIconComplete.getComponent(Renderable).visible = !params.fail;
        const goalIconFail = this.element.find(GOAL_ICON_FAIL);
        goalIconFail.getComponent(Renderable).visible = params.fail;

        // Set button visibility.
        const btnChallenge = this.element.find(BTN_CHALLENGE);
        const btnContinue = this.element.find(BTN_CONTINUE);
        const btnReplay = this.element.find(BTN_REPLAY);
        const btnClose = this.element.find(BTN_CLOSE);
        btnChallenge.getComponent(Renderable).visible = !params.fail;
        btnContinue.getComponent(Renderable).visible = !params.fail;
        btnReplay.getComponent(Renderable).visible = params.fail;
        btnClose.getComponent(Renderable).visible = params.fail;

        // Move continue button and disable challenge button if social features are disabled
        if (!Game.sdk.driver.hasSocialFeaturesSupport()) {
            btnContinue.container.x = btnReplay.container.x;
            btnChallenge.getComponent(Renderable).visible = false;
        }

        // Remove the "Challenge Friend" button on Win Screen for level 1, 2 and 3.
        if (!params.fail && params.levelNumber <= 3) {
            btnChallenge.getComponent(Renderable).visible = false;
            btnContinue.getComponent(ElementComponent).anchorX1 = 0.5;
            btnContinue.getComponent(ElementComponent).anchorX2 = 0.5;
        }

        // Set text values.
        const txtScore = this.element.find(TXT_SCORE);
        txtScore.getComponent(UITextComponent).text = '0';
        const txtReward = this.element.find(TXT_COINS);
        txtReward.getComponent(UITextComponent).text = '0';
        const topList = this.element.find(TOP_LIST);
        topList.getComponent(ElementComponent).pivot.x = 1;

        LeaderboardHelper.loadLevelMenuLeaderboard(
            topList,
            params.levelNumber.toString()
        );

        if (params.levelNumber > 0 && params.levelNumber <= 10) {
            Game.sdk.gameTracking(
                [
                    FBTrackingEvent.FTUE_06_Level01FinishWindowShow,
                    FBTrackingEvent.FTUE_15_Level02FinishWindowShow,
                    FBTrackingEvent.FTUE_23_Level03FinishWindowShow,
                    FBTrackingEvent.Level4_WinScreenShow,
                    FBTrackingEvent.Level5_WinScreenShow,
                    FBTrackingEvent.Level6_WinScreenShow,
                    FBTrackingEvent.Level7_WinScreenShow,
                    FBTrackingEvent.Level8_WinScreenShow,
                    FBTrackingEvent.Level9_WinScreenShow,
                    FBTrackingEvent.Level10_WinScreenShow,
                ][params.levelNumber - 1]
            );
        }
        const event = ASOLILevelEventsMap.get(params.levelNumber);
        if (event) {
            Game.sdk.gameTracking(event);
        }

        if (params.fail) {
            StartAudio(
                this.system.scene,
                AUDIO_LEVEL_FAILED,
                AudioTrackType.SFX
            );
        }
    }

    protected onShown(params?: any): void {
        // Show top list.
        const topList = this.element.find(TOP_LIST);
        if (topList && Game.sdk.driver.hasSocialFeaturesSupport()) {
            StartAnimation(this.element.find(TOP_LIST), 'TopList');
        }

        const levelState = this.system.state.getState().levelState;
        const completeData = levelState.completeData;
        let score: number = completeData && completeData.score;
        let stars: number = completeData && completeData.stars;
        let coins: number = completeData && completeData.coins;

        if (params.fail) {
            score = levelState.score;
            stars = 0;
            coins = 0;
        }

        // Play stars animation and score/coins counting up.
        StartAnimation(this.element, 'LevelCompleteMenu', {
            scene: this.system.scene,
            fail: false,
            score: score,
            coins: coins,
            stars: stars,
        });
    }

    protected onCreate(): void {
        // Button challenge.
        const btnChallenge = this.element.find(BTN_CHALLENGE);
        btnChallenge.on(GameObjectEvent.SELECTED, () => {
            this.system.state.dispatch(
                GameActions.setContext(ContextType.CHALLENGE_FRIEND)
            );
        });
        applyDefaultButtonAnimation(btnChallenge, AUDIO_UI_BUTTON_DEFAULT);

        // Button continue.
        const btnContinue = this.element.find(BTN_CONTINUE);
        btnContinue.on(GameObjectEvent.SELECTED, () => {
            if (this.levelNumber === 1) {
                Game.sdk.gameTracking(
                    FBTrackingEvent.FTUE_07_TapOnButtonContinue
                );
            }

            this.system.state.dispatch(
                GameActions.endLevel(LevelStatus.EXITING)
            );
        });
        applyBounceButtonAnimation(btnContinue, AUDIO_UI_BUTTON_DEFAULT);

        // Button replay.
        const btnReplay = this.element.find(BTN_REPLAY);
        btnReplay.on(GameObjectEvent.SELECTED, () => {
            this.system.state.dispatch(
                GameActions.endLevel(LevelStatus.RESTARTING)
            );
        });
        applyBounceButtonAnimation(btnReplay, AUDIO_UI_BUTTON_DEFAULT);

        // Button close.
        const btnClose = this.element.find(BTN_CLOSE);
        btnClose.on(GameObjectEvent.SELECTED, () => {
            this.system.state.dispatch(
                GameActions.endLevel(LevelStatus.EXITING)
            );
        });
        applyDefaultButtonAnimation(btnClose, AUDIO_UI_BUTTON_CLOSE);
    }
}
